import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getMyPets, createPet, getPet, updatePet, deletePet,
  regeneratePetQR, registerAssetToPet
} from '../services/petApi';
import PetPhotoManager from './PetPhotoManager';
import PetDocumentManager from './PetDocumentManager';
import NfcOfflineActions from './NfcOfflineActions';
import config from '../config/appConfig';
import '../styles/PetManager.css';

// ── Constants ─────────────────────────────────────────────────
const SPECIES_OPTS = [
  { value: 'dog',          label: 'Dog',          icon: '🐕' },
  { value: 'cat',          label: 'Cat',          icon: '🐈' },
  { value: 'bird',         label: 'Bird',         icon: '🐦' },
  { value: 'rabbit',       label: 'Rabbit',       icon: '🐇' },
  { value: 'reptile',      label: 'Reptile',      icon: '🦎' },
  { value: 'fish',         label: 'Fish',         icon: '🐠' },
  { value: 'small_animal', label: 'Small Animal', icon: '🐹' },
  { value: 'horse',        label: 'Horse',        icon: '🐴' },
  { value: 'other',        label: 'Other',        icon: '🐾' },
];

const SEX_OPTS = [
  { value: 'unknown',        label: 'Unknown' },
  { value: 'male',           label: 'Male' },
  { value: 'female',         label: 'Female' },
  { value: 'neutered_male',  label: 'Neutered Male' },
  { value: 'spayed_female',  label: 'Spayed Female' },
];

const speciesIcon = v => SPECIES_OPTS.find(s => s.value === v)?.icon ?? '🐾';
const speciesLabel = v => SPECIES_OPTS.find(s => s.value === v)?.label ?? 'Pet';

// ── Blank templates ───────────────────────────────────────────
const BLANK_MEDICAL = {
  species:'other', breed:'', colour:'', sex:'unknown',
  dateOfBirth:'', weight:'', microchipNumber:'',
  bloodType:'', allergies:[], medications:[], conditions:[],
  vaccinations:[], vetName:'', vetPhone:'', vetClinic:'',
  dietNotes:'', notes:''
};

const BLANK_PUBLIC = {
  enabled:false, showOwnerName:true, showOwnerContact:true,
  showSpeciesBreed:true, showDateOfBirth:false, showMicrochip:false,
  showBloodType:true, showAllergies:true, showMedications:false,
  showConditions:true, showVaccinations:true, showVet:true, customMessage:''
};

// ── Small helpers ─────────────────────────────────────────────
const TagInput = ({ label, values = [], onChange, placeholder }) => {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (v && !values.includes(v)) { onChange([...values, v]); }
    setInput('');
  };
  return (
    <div className="pm-tag-field">
      <label className="pm-label">{label}</label>
      <div className="pm-tags">
        {values.map((v, i) => (
          <span key={i} className="pm-tag">
            {v}
            <button type="button" onClick={() => onChange(values.filter((_, j) => j !== i))}>✕</button>
          </span>
        ))}
      </div>
      <div className="pm-tag-input-row">
        <input
          className="pm-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
        />
        <button type="button" className="pm-btn pm-btn-ghost pm-btn-sm" onClick={add}>Add</button>
      </div>
    </div>
  );
};

const Toggle = ({ on, label, onToggle }) => (
  <div className="pm-toggle-row">
    <span>{label}</span>
    <button
      type="button"
      className={`pm-toggle ${on ? 'on' : ''}`}
      onClick={onToggle}
      aria-label={`${on ? 'Disable' : 'Enable'} ${label}`}
    >
      <span className="pm-toggle-thumb" />
    </button>
  </div>
);

// ── Pet card (list view) ──────────────────────────────────────
const PetCard = ({ pet, onSelect, onDelete }) => (
  <div className="pm-pet-card" onClick={() => onSelect(pet._id)}>
    <div className="pm-pet-card-icon">{speciesIcon(pet.medicalInfo?.species)}</div>
    <div className="pm-pet-card-body">
      <h3>{pet.name}</h3>
      <p>{speciesLabel(pet.medicalInfo?.species)}{pet.medicalInfo?.breed ? ` · ${pet.medicalInfo.breed}` : ''}</p>
      <span className={`pm-pill ${pet.publicProfile?.enabled ? 'green' : 'grey'}`}>
        {pet.publicProfile?.enabled ? 'Public' : 'Private'}
      </span>
    </div>
    <button
      className="pm-btn pm-btn-ghost pm-btn-sm pm-card-del"
      onClick={e => { e.stopPropagation(); onDelete(pet); }}
      title="Remove pet"
    >🗑</button>
  </div>
);

// ── Pet detail / edit panel ───────────────────────────────────
const PetDetail = ({ petId, onBack, onSaved }) => {
  const [pet, setPet]       = useState(null);
  const [tab, setTab]       = useState('info');
  const [form, setForm]     = useState({ name: '', medicalInfo: BLANK_MEDICAL, publicProfile: BLANK_PUBLIC });
  const [saving, setSaving] = useState(false);
  const [assetInput, setAssetInput] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);

  const load = useCallback(async () => {
    const res = await getPet(petId);
    const p   = res.data.pet;
    setPet(p);
    setForm({
      name: p.name,
      medicalInfo:   { ...BLANK_MEDICAL,  ...p.medicalInfo  },
      publicProfile: { ...BLANK_PUBLIC,   ...p.publicProfile }
    });
  }, [petId]);

  useEffect(() => { load(); }, [load]);

  const setMI = patch => setForm(f => ({ ...f, medicalInfo:   { ...f.medicalInfo,   ...patch } }));
  const setPP = patch => setForm(f => ({ ...f, publicProfile: { ...f.publicProfile, ...patch } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePet(petId, form);
      toast.success(`${form.name} updated`);
      onSaved();
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleRegenQR = async () => {
    if (!window.confirm('Generate a new QR code? The old QR code will stop working.')) return;
    setRegenLoading(true);
    try {
      const res = await regeneratePetQR(petId);
      setPet(res.data.pet);
      toast.success('New QR code generated');
    } catch { toast.error('Failed to regenerate QR'); }
    finally { setRegenLoading(false); }
  };

  const handleRegisterAsset = async e => {
    e.preventDefault();
    setRegLoading(true);
    try {
      await registerAssetToPet(petId, assetInput.trim().toUpperCase());
      toast.success('Physical asset linked to pet!');
      setAssetInput('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally { setRegLoading(false); }
  };

  if (!pet) return <div className="pm-loading">Loading…</div>;

  const mi = form.medicalInfo;
  const pp = form.publicProfile;

  const TABS = [
    { key: 'info',      label: 'Details'   },
    { key: 'medical',   label: 'Medical'   },
    { key: 'photos',    label: 'Photos'    },
    { key: 'documents', label: 'Documents' },
    { key: 'qr',        label: 'QR Code'   },
    { key: 'privacy',   label: 'Privacy'   },
  ];

  return (
    <div className="pm-detail">
      {/* Header */}
      <div className="pm-detail-header">
        <button className="pm-back" onClick={onBack}>← Back</button>
        <span className="pm-detail-icon">{speciesIcon(mi.species)}</span>
        <div>
          <h2>{pet.name}</h2>
          <p className="pm-detail-sub">{speciesLabel(mi.species)}{mi.breed ? ` · ${mi.breed}` : ''}</p>
        </div>
        <button className="pm-btn pm-btn-primary pm-ml-auto" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {/* Tabs */}
      <div className="pm-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`pm-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Details tab ──────────────────────────────────── */}
      {tab === 'info' && (
        <div className="pm-section">
          <div className="pm-field">
            <label className="pm-label">Pet Name *</label>
            <input className="pm-input" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} maxLength={64} />
          </div>

          <div className="pm-grid2">
            <div className="pm-field">
              <label className="pm-label">Species</label>
              <select className="pm-input" value={mi.species} onChange={e => setMI({ species: e.target.value })}>
                {SPECIES_OPTS.map(s => <option key={s.value} value={s.value}>{s.icon} {s.label}</option>)}
              </select>
            </div>
            <div className="pm-field">
              <label className="pm-label">Breed</label>
              <input className="pm-input" value={mi.breed} onChange={e => setMI({ breed: e.target.value })} placeholder="e.g. Labrador" />
            </div>
            <div className="pm-field">
              <label className="pm-label">Sex</label>
              <select className="pm-input" value={mi.sex} onChange={e => setMI({ sex: e.target.value })}>
                {SEX_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="pm-field">
              <label className="pm-label">Colour</label>
              <input className="pm-input" value={mi.colour} onChange={e => setMI({ colour: e.target.value })} placeholder="e.g. Golden" />
            </div>
            <div className="pm-field">
              <label className="pm-label">Date of Birth</label>
              <input className="pm-input" type="date" value={mi.dateOfBirth ? mi.dateOfBirth.toString().slice(0,10) : ''}
                onChange={e => setMI({ dateOfBirth: e.target.value })} />
            </div>
            <div className="pm-field">
              <label className="pm-label">Weight</label>
              <input className="pm-input" value={mi.weight} onChange={e => setMI({ weight: e.target.value })} placeholder="e.g. 12 kg" />
            </div>
            <div className="pm-field">
              <label className="pm-label">Microchip #</label>
              <input className="pm-input" value={mi.microchipNumber} onChange={e => setMI({ microchipNumber: e.target.value })} />
            </div>
          </div>
        </div>
      )}

      {/* ── Medical tab ────────────────────────────────── */}
      {tab === 'medical' && (
        <div className="pm-section">
          <div className="pm-field">
            <label className="pm-label">Blood Type</label>
            <input className="pm-input" value={mi.bloodType} onChange={e => setMI({ bloodType: e.target.value })} placeholder="e.g. DEA 1.1+" />
          </div>

          <TagInput label="Allergies" values={mi.allergies} onChange={v => setMI({ allergies: v })} placeholder="Type allergy and press Enter" />
          <TagInput label="Medical Conditions" values={mi.conditions} onChange={v => setMI({ conditions: v })} placeholder="Type condition and press Enter" />

          <div className="pm-section-divider">Vet Details</div>
          <div className="pm-grid2">
            <div className="pm-field">
              <label className="pm-label">Vet Name</label>
              <input className="pm-input" value={mi.vetName} onChange={e => setMI({ vetName: e.target.value })} />
            </div>
            <div className="pm-field">
              <label className="pm-label">Vet Clinic</label>
              <input className="pm-input" value={mi.vetClinic} onChange={e => setMI({ vetClinic: e.target.value })} />
            </div>
            <div className="pm-field">
              <label className="pm-label">Vet Phone</label>
              <input className="pm-input" type="tel" value={mi.vetPhone} onChange={e => setMI({ vetPhone: e.target.value })} />
            </div>
          </div>

          <div className="pm-field">
            <label className="pm-label">Diet Notes</label>
            <textarea className="pm-input" rows={2} value={mi.dietNotes} onChange={e => setMI({ dietNotes: e.target.value })} placeholder="Dietary requirements, feeding schedule…" />
          </div>
          <div className="pm-field">
            <label className="pm-label">General Notes</label>
            <textarea className="pm-input" rows={3} value={mi.notes} onChange={e => setMI({ notes: e.target.value })} placeholder="Any other important information…" />
          </div>
        </div>
      )}

      {/* ── Photos tab ───────────────────────────────── */}
      {tab === 'photos' && (
        <PetPhotoManager petId={petId} petName={pet.name} />
      )}

      {/* ── Documents tab ────────────────────────────── */}
      {tab === 'documents' && (
        <PetDocumentManager petId={petId} />
      )}

      {/* ── QR Code tab ──────────────────────────────── */}
      {tab === 'qr' && (
        <div className="pm-section">
          <div className="pm-qr-wrap">
            {pet.qrCode ? (
              <img src={pet.qrCode} alt={`QR code for ${pet.name}`} className="pm-qr-img" />
            ) : (
              <div className="pm-qr-placeholder">No QR code yet</div>
            )}
            <p className="pm-qr-url">{pet.qrCodeUrl || '—'}</p>
          </div>

          <div className="pm-qr-actions">
            {pet.qrCode && (
              <a
                href={pet.qrCode}
                download={`${pet.name}-qr.png`}
                className="pm-btn pm-btn-secondary"
              >
                ⬇ Download QR
              </a>
            )}
            <button
              className="pm-btn pm-btn-ghost"
              onClick={handleRegenQR}
              disabled={regenLoading}
            >
              {regenLoading ? 'Generating…' : '↺ New QR Code'}
            </button>
          </div>

          <div className="pm-section-divider">Link a Physical Device</div>
          <p className="pm-hint">
            Have a physical {config.assetPrefix}-ID tag or card? Enter the asset ID printed
            on it to link it to {pet.name}.
          </p>
          <form onSubmit={handleRegisterAsset} className="pm-asset-row">
            <input
              className="pm-input pm-mono"
              value={assetInput}
              onChange={e => setAssetInput(e.target.value.toUpperCase())}
              placeholder={`${config.assetPrefix}-XXXXXXXX`}
              pattern="[A-Z0-9-]+"
              style={{ flex: 1 }}
            />
            <button type="submit" className="pm-btn pm-btn-primary" disabled={regLoading || !assetInput}>
              {regLoading ? 'Linking…' : 'Link Device'}
            </button>
          </form>

          {pet.registeredAssets?.length > 0 && (
            <div className="pm-linked-assets">
              <p className="pm-label">Linked Devices ({pet.registeredAssets.length})</p>
              {pet.registeredAssets.map(a => (
                <span key={a._id || a} className="pm-tag">{typeof a === 'object' ? a.assetId : a}</span>
              ))}
            </div>
          )}

          {pet.qrCodeId && (
            <NfcOfflineActions
              profileUrl={pet.qrCodeUrl || `${window.location.origin}/pet/${pet.qrCodeId}`}
              offlineUrl={`/api/offline/pet/${pet.qrCodeId}`}
              profileName={pet.name}
              type="pet"
            />
          )}
        </div>
      )}

      {/* ── Privacy tab ──────────────────────────────── */}
      {tab === 'privacy' && (
        <div className="pm-section">
          <div className="pm-privacy-intro">
            <h3>Public Profile</h3>
            <p>Control what information is visible when {pet.name}'s QR code is scanned by anyone.</p>
          </div>

          <Toggle on={pp.enabled} label="Enable public profile"
            onToggle={() => setPP({ enabled: !pp.enabled })} />

          {pp.enabled && (
            <div className="pm-toggles">
              <p className="pm-toggles-hint">When enabled, show:</p>
              <Toggle on={pp.showOwnerName}    label="Owner name"         onToggle={() => setPP({ showOwnerName:    !pp.showOwnerName    })} />
              <Toggle on={pp.showOwnerContact} label="Owner contact email" onToggle={() => setPP({ showOwnerContact: !pp.showOwnerContact })} />
              <Toggle on={pp.showSpeciesBreed} label="Species &amp; breed" onToggle={() => setPP({ showSpeciesBreed: !pp.showSpeciesBreed })} />
              <Toggle on={pp.showDateOfBirth}  label="Date of birth"      onToggle={() => setPP({ showDateOfBirth:  !pp.showDateOfBirth  })} />
              <Toggle on={pp.showMicrochip}    label="Microchip number"   onToggle={() => setPP({ showMicrochip:    !pp.showMicrochip    })} />
              <Toggle on={pp.showBloodType}    label="Blood type"         onToggle={() => setPP({ showBloodType:    !pp.showBloodType    })} />
              <Toggle on={pp.showAllergies}    label="Allergies"          onToggle={() => setPP({ showAllergies:    !pp.showAllergies    })} />
              <Toggle on={pp.showMedications}  label="Medications"        onToggle={() => setPP({ showMedications:  !pp.showMedications  })} />
              <Toggle on={pp.showConditions}   label="Medical conditions" onToggle={() => setPP({ showConditions:   !pp.showConditions   })} />
              <Toggle on={pp.showVaccinations} label="Vaccinations"       onToggle={() => setPP({ showVaccinations: !pp.showVaccinations })} />
              <Toggle on={pp.showVet}          label="Vet details"        onToggle={() => setPP({ showVet:          !pp.showVet          })} />

              <div className="pm-field" style={{ marginTop: '1rem' }}>
                <label className="pm-label">Emergency / Custom Message</label>
                <textarea className="pm-input" rows={3}
                  value={pp.customMessage}
                  onChange={e => setPP({ customMessage: e.target.value })}
                  maxLength={512}
                  placeholder="e.g. If found call Jane: 07700 900000. Needs daily insulin." />
                <small className="pm-hint">{pp.customMessage.length}/512</small>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main export ───────────────────────────────────────────────
export default function PetManager() {
  const [pets, setPets]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showAdd, setShowAdd]   = useState(false);
  const [newName, setNewName]   = useState('');
  const [newSpecies, setNewSpecies] = useState('dog');
  const [adding, setAdding]     = useState(false);

  const loadPets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyPets();
      setPets(res.data.pets);
    } catch { toast.error('Failed to load pets'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPets(); }, [loadPets]);

  const handleAdd = async e => {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const res = await createPet({
        name: newName.trim(),
        medicalInfo: { species: newSpecies }
      });
      toast.success(`${res.data.pet.name} added!`);
      setPets(p => [res.data.pet, ...p]);
      setNewName('');
      setNewSpecies('dog');
      setShowAdd(false);
      setSelectedId(res.data.pet._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add pet');
    } finally { setAdding(false); }
  };

  const handleDelete = async pet => {
    if (!window.confirm(`Remove ${pet.name}? This cannot be undone.`)) return;
    try {
      await deletePet(pet._id);
      toast.success(`${pet.name} removed`);
      setPets(p => p.filter(x => x._id !== pet._id));
      if (selectedId === pet._id) setSelectedId(null);
    } catch { toast.error('Failed to remove pet'); }
  };

  if (selectedId) {
    return (
      <PetDetail
        petId={selectedId}
        onBack={() => setSelectedId(null)}
        onSaved={loadPets}
      />
    );
  }

  return (
    <div className="pm-root">
      {/* Header row */}
      <div className="pm-header">
        <h2>My Pets</h2>
        <button className="pm-btn pm-btn-primary" onClick={() => setShowAdd(v => !v)}>
          {showAdd ? 'Cancel' : '+ Add Pet'}
        </button>
      </div>

      {/* Add pet form */}
      {showAdd && (
        <form onSubmit={handleAdd} className="pm-add-form">
          <h3>Add a New Pet</h3>
          <div className="pm-grid2">
            <div className="pm-field">
              <label className="pm-label">Pet Name *</label>
              <input
                className="pm-input"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Buddy"
                maxLength={64}
                required
                autoFocus
              />
            </div>
            <div className="pm-field">
              <label className="pm-label">Species</label>
              <select className="pm-input" value={newSpecies} onChange={e => setNewSpecies(e.target.value)}>
                {SPECIES_OPTS.map(s => <option key={s.value} value={s.value}>{s.icon} {s.label}</option>)}
              </select>
            </div>
          </div>
          <p className="pm-hint">You can add full details after creating the pet.</p>
          <button type="submit" className="pm-btn pm-btn-primary" disabled={adding || !newName.trim()}>
            {adding ? 'Adding…' : 'Add Pet'}
          </button>
        </form>
      )}

      {/* Pet list */}
      {loading ? (
        <div className="pm-loading">Loading pets…</div>
      ) : pets.length === 0 ? (
        <div className="pm-empty">
          <span className="pm-empty-icon">🐾</span>
          <p>No pets yet. Add your first pet to get started.</p>
        </div>
      ) : (
        <div className="pm-list">
          {pets.map(pet => (
            <PetCard
              key={pet._id}
              pet={pet}
              onSelect={setSelectedId}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
